import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { PortalClaimDto, SupplierResponse } from "@hisense/shared";
import { apiFetch, getLocalizedError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { FieldLabel } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { ErrorState } from "@/components/ui/States";
import { formatMoney } from "@/domain/money";
import { formatDateTime } from "@/domain/datetime";

const claimsPage = (supplierId: string, signal?: AbortSignal) => apiFetch<{ data: PortalClaimDto[] }>("/portal/claims", { query: { supplierId }, signal });

/**
 * Khiếu nại Hisense Button gửi nhà cung cấp, trên Cổng nhà cung cấp (17/09/2026).
 *
 * Nhà cung cấp đọc được họ bị đòi gì, vì sao, cho sản phẩm / lô / đơn mua nào, và tự trả lời
 * Chấp nhận hoặc Không đồng ý kèm giải thích. Máy chủ đã lọc danh sách trắng: không có tên khách
 * hàng hay ghi chú nội bộ. Nhân viên xem thay (chọn nhà cung cấp) thì chỉ đọc — phản hồi thay nhà
 * cung cấp là việc của màn hình nội bộ ("Ghi nhận phản hồi").
 *
 * Không có khiếu nại nào thì KHÔNG hiện mục này (kể cả lúc đang tải): khiếu nại là chuyện bất
 * thường, một ô "Không có khiếu nại" trên cổng của mọi nhà cung cấp chỉ gây thắc mắc. Lỗi tải thì
 * vẫn hiện, để nhà cung cấp biết mà thử lại.
 */
export function SupplierClaimsPanel({ supplierId, canRespond }: { supplierId: string; canRespond: boolean }) {
  const { t, i18n } = useTranslation();
  const query = useQuery({ queryKey: ["portal-claims", supplierId], enabled: supplierId.length > 0, queryFn: ({ signal }) => claimsPage(supplierId, signal) });
  const [responding, setResponding] = useState<{ row: PortalClaimDto; response: SupplierResponse } | null>(null);
  if (!query.error && !query.data?.data.length) return null;
  return (
    <section className="mt-4 rounded border border-line">
      <div className="border-b border-line bg-sunken p-3">
        <h2 className="text-sm font-medium">{t("portal.claims")}</h2>
        <p className="text-xs text-ink-soft">{t("portal.claimsIntro")}</p>
      </div>
      {query.error || !query.data ? (
        <ErrorState message={t("portal.claimsError")} onRetry={() => void query.refetch()} />
      ) : (
        <ul className="divide-y divide-line">
          {query.data.data.map((c) => (
            <li key={c.id} className="grid gap-3 p-3 text-sm md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
              <div className="min-w-0">
                <p>
                  <span className="font-mono">{c.claimNo}</span> · {t(`portal.claimStatuses.${c.status}`)}
                </p>
                <p className="font-medium">
                  {t(`claims.issues.${c.issueCode}.label`)} · {t(`claims.severities.${c.severity}`)}
                </p>
                <p className="mt-1 whitespace-pre-wrap">{c.description}</p>
                {(c.productCode || c.lotNumber || c.poNo) && (
                  <p className="mt-1 font-mono text-xs text-ink-soft">{[c.productCode, c.lotNumber, c.poNo].filter(Boolean).join(" · ")}</p>
                )}
                <ul className="mt-2 list-disc pl-5">
                  {c.claims.map((line) => (
                    <li key={line.type}>
                      {t(`claims.types.${line.type}`)}
                      {line.amountMinor != null && c.currency && <span className="font-mono tabular"> · {formatMoney(line.amountMinor, c.currency)}</span>}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs text-ink-soft">{t("portal.claimSent")}: {formatDateTime(c.sentAt, i18n.language)}</p>
              </div>
              <div className="min-w-0 space-y-2">
                {c.claimedMinor > 0 && c.currency && (
                  <p>
                    {t("supplierClaims.claimed")}: <span className="font-mono tabular">{formatMoney(c.claimedMinor, c.currency)}</span>
                  </p>
                )}
                {c.agreedMinor != null && (
                  <p className="font-medium">
                    {t("portal.claimAgreed")}: <span className="font-mono tabular">{c.currency ? formatMoney(c.agreedMinor, c.currency) : "—"}</span>
                  </p>
                )}
                {/* Chỉ khi phiếu ghi nợ đã vào sổ: lúc đó khoản trừ mới hiện trên bảng kê công nợ bên dưới. */}
                {c.debitNoteNo && <p className="text-xs text-ink-soft">{t("portal.claimDebitNote", { no: c.debitNoteNo })}</p>}
                {c.supplierResponse && (
                  <div>
                    <p>
                      {t("portal.claimResponse")}: {t(`supplierClaims.responses.${c.supplierResponse}`)}
                    </p>
                    {c.supplierResponseNote && <p className="whitespace-pre-wrap text-xs text-ink-soft">{c.supplierResponseNote}</p>}
                  </div>
                )}
                {canRespond && c.status === "Sent" && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => setResponding({ row: c, response: "Accepted" })}>{t("portal.acceptClaim")}</Button>
                    <Button size="sm" variant="secondary" onClick={() => setResponding({ row: c, response: "Disputed" })}>{t("portal.disputeClaim")}</Button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {responding && <RespondDialog key={`${responding.row.id}:${responding.response}`} row={responding.row} response={responding.response} supplierId={supplierId} onClose={() => setResponding(null)} />}
    </section>
  );
}

function RespondDialog({ row, response, supplierId, onClose }: { row: PortalClaimDto; response: SupplierResponse; supplierId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lock = useRef(false);
  const submit = async () => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await apiFetch<{ data: PortalClaimDto[] }>(`/portal/claims/${row.id}/response`, { method: "POST", body: { revision: row.revision, response, note } });
      client.setQueryData(["portal-claims", supplierId], result);
      onClose();
    } catch (e) {
      setError(getLocalizedError(e, t, "portal.claimsError"));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const needsNote = response === "Disputed";
  return (
    <Modal title={t(response === "Accepted" ? "portal.acceptClaim" : "portal.disputeClaim")} eyebrow={row.claimNo} closeLabel={t("common.close")} onClose={() => { if (!busy) onClose(); }} className="max-w-lg">
      <div className="min-h-0 space-y-3 overflow-y-auto p-4">
        <p className="text-sm">{t(response === "Accepted" ? "portal.acceptHint" : "portal.disputeHint")}</p>
        <div>
          <FieldLabel htmlFor="portal-claim-note">{t("portal.claimNote")}</FieldLabel>
          <textarea id="portal-claim-note" className="min-h-24 w-full resize-y rounded-sm border border-line-strong bg-surface p-3 text-sm outline-brand" value={note} maxLength={2000} disabled={busy} onChange={(e) => setNote(e.target.value)} />
        </div>
        {error && <p role="alert" className="text-sm text-danger">{error}</p>}
      </div>
      <div className="flex shrink-0 justify-end gap-2 border-t border-line p-4">
        <Button variant="secondary" disabled={busy} onClick={onClose}>{t("common.cancel")}</Button>
        <Button disabled={busy || (needsNote && note.trim().length < 5)} onClick={() => void submit()}>{t("portal.sendResponse")}</Button>
      </div>
    </Modal>
  );
}
